import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ProductRow } from '../api';

export function ProductsPage() {
  const [q, setQ] = useState('');
  const [onlyRrpViolation, setOnlyRrpViolation] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['products', q],
    queryFn: () => api.products({ q, limit: 200 }),
  });
  const items = onlyRrpViolation ? data?.items.filter((p) => p.rrpViolation) : data?.items;

  return (
    <>
      <div className="page-header">
        <h2>Товары</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyRrpViolation} onChange={(e) => setOnlyRrpViolation(e.target.checked)} />
            {' '}Только нарушения РРЦ
          </label>
          <input
            className="search"
            placeholder="Поиск по названию или артикулу"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {isLoading && <div className="card">Загрузка…</div>}
      {error && <div className="card" style={{ color: 'var(--red)' }}>{String((error as Error).message)}</div>}

      {data && (
        <>
          <div className="muted" style={{ marginBottom: 8 }}>
            Найдено: {data.total}
            {onlyRrpViolation && items && ` · показано с нарушением РРЦ: ${items.length}`}
          </div>
          <table className="products">
            <thead>
              <tr>
                <th style={{ width: 60 }}></th>
                <th>Название</th>
                <th>Артикул / SKU</th>
                <th className="num">Цена</th>
                <th className="num">Маркет.</th>
                <th>Индекс</th>
                <th className="num" title="Минимальная цена на других площадках (Wildberries, Я.Маркет и др.)">Min внешн.</th>
                <th className="num" title="Минимальная цена среди других продавцов Ozon на этот же товар">Min Ozon</th>
                <th className="num" title="Минимальная цена по вашим же карточкам на других маркетплейсах">Min МП</th>
                <th className="num" title="Рекомендованная розничная цена из 1С">РРЦ</th>
                <th className="num">Остаток</th>
              </tr>
            </thead>
            <tbody>
              {items?.map((p) => <ProductRow key={p.productId} p={p} />)}
              {items?.length === 0 && (
                <tr><td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  {onlyRrpViolation ? 'Нарушений РРЦ не обнаружено.' : 'Каталог пуст. Запустите синк товаров в разделе «Синхронизация».'}
                </td></tr>
              )}
            </tbody>
          </table>
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            <strong>Цены конкурентов:</strong> Ozon API не отдаёт сырые цены отдельных продавцов, только агрегированные минимумы по каналам. Колонка «Min внешн.» — цена конкурента вне Ozon (WB, ЯМ и др.), «Min Ozon» — минимум среди других продавцов на ту же карточку Ozon.
          </div>
        </>
      )}
    </>
  );
}

function ProductRow({ p }: { p: ProductRow }) {
  return (
    <tr>
      <td>{p.image ? <img src={p.image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : null}</td>
      <td>
        <div style={{ fontWeight: 500 }}>{p.name}</div>
        <div className="muted" style={{ fontSize: 12 }}>{p.brand}</div>
      </td>
      <td>
        <div>{p.offerId}</div>
        <div className="muted" style={{ fontSize: 12 }}>{p.sku ?? '—'}</div>
      </td>
      <td className="num">
        {p.price ? `${p.price} ₽` : '—'}
        {p.oldPrice && <div className="muted" style={{ fontSize: 12, textDecoration: 'line-through' }}>{p.oldPrice} ₽</div>}
      </td>
      <td className="num">{p.marketingPrice ? `${p.marketingPrice} ₽` : '—'}</td>
      <td>
        {p.colorIndex ? (
          <>
            <span className={`color-dot ${p.colorIndex}`} />
            <span className="muted" style={{ fontSize: 12 }}>{p.colorIndex}</span>
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num">{fmtMin(p.extMinPrice, p.price)}</td>
      <td className="num">{fmtMin(p.ozonMinPrice, p.price)}</td>
      <td className="num">{fmtMin(p.mpMinPrice, p.price)}</td>
      <td className="num">
        {p.rrpPrice ? (
          <>
            <div>{Number(p.rrpPrice).toFixed(0)} ₽</div>
            {p.rrpViolation && (
              <div style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }} title={`Эффективная клиентская: ${p.rrpViolation.effective} ₽, ниже РРЦ на ${(p.rrpViolation.gapPct * 100).toFixed(1)}%`}>
                ⚠ {p.rrpViolation.effective} ₽ ({(p.rrpViolation.gapPct * 100).toFixed(0)}% ниже)
              </div>
            )}
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num">{p.stockTotal}</td>
    </tr>
  );
}

function fmtMin(competitor: string | null, ours: string | null): React.ReactNode {
  if (!competitor || competitor === '0' || competitor === '0.00') return <span className="muted">—</span>;
  const c = Number(competitor);
  const o = ours ? Number(ours) : null;
  if (!Number.isFinite(c) || c <= 0) return <span className="muted">—</span>;
  const color = o == null ? undefined : c < o ? 'var(--red)' : c > o ? 'var(--green)' : undefined;
  return <span style={{ color }}>{c.toFixed(0)} ₽</span>;
}
